import { omit, map } from 'lodash';
import { Router } from '@angular/router';
import { Inject, Injectable } from '@angular/core';
import { map as rxMap, switchMap, take, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, forkJoin, Observable, of, Subject, Subscription, throwError } from 'rxjs';

import { StorageHelper } from '@diktup/frontend/helpers';
import {
  UserRole,
  UserType,
  UserStatus,
  PhoneInput,
  CurrentUserGQL,
  TargetAciInput,
  DeleteUserGQL,
  LoginForTargetGQL,
  UserWithTargetInput,
  RegisterForTargetGQL,
  DeleteUserReasonEnum,
  UserEventsSourceEnum,
  IsLoginForTargetExistGQL,
  InvitePartnerToNetworkGQL,
  ResetPasswordForTargetGQL,
  UpdateCurrentUserPasswordGQL,
  SaveCurrentCorporateUserStatusGQL,
  SendForgotPasswordMailToTargetGQL,
  RegisterForTargetWithWholesalerGQL,

} from '@sifca-monorepo/ecommerce-generator';

import { AuthUtils } from './auth.utils';
import { AppService } from '../app.service';
import { UserService } from '../user/user.service';
import { OrdersService } from '../order';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  protected unsubscribeAll: Subject<any> = new Subject<any>();
  private authenticated: BehaviorSubject<boolean> = new BehaviorSubject(null);

  user: UserType;
  timer: Subscription;
  mouseEvents: Subscription;

  get authenticated$(): Observable<boolean> {
    return this.authenticated.asObservable();
  }
  set authenticated$(value: any) {
    this.authenticated.next(value);
  }
  set accessToken(token: string) {
    this.storageHelper.setData({ [this.ACCESS_TOKEN]: token });
  }
  get accessToken(): string {
    return this.storageHelper.getData(this.ACCESS_TOKEN);
  }

  constructor(
    private router: Router,
    private appService: AppService,
    private userService: UserService,
    private orderService: OrdersService,
    private deleteUserGQL: DeleteUserGQL,
    private storageHelper: StorageHelper,
    private currentUserGQL: CurrentUserGQL,
    private loginForTargetGQL: LoginForTargetGQL,
    private registerForTargetGQL: RegisterForTargetGQL,
    @Inject('ACCESS_TOKEN') private ACCESS_TOKEN: string,
    private resetPasswordForAppGql: ResetPasswordForTargetGQL,
    private invitePartnerToNetworkGQL: InvitePartnerToNetworkGQL,
    private isLoginForTargetExistGQL: IsLoginForTargetExistGQL,
    private resetPasswordForTargetGQL: ResetPasswordForTargetGQL,
    private updateCurrentUserPasswordGQL: UpdateCurrentUserPasswordGQL,
    private sendForgotPasswordMailToTargetGQL: SendForgotPasswordMailToTargetGQL,
    private saveCurrentCorporateUserStatusGQL: SaveCurrentCorporateUserStatusGQL,
    private registerForTargetWithWholesalerGQL: RegisterForTargetWithWholesalerGQL,
  ) {
    const accessToken = this.storageHelper.getData(this.ACCESS_TOKEN);
    if (accessToken) {
      this.accessToken = accessToken;
      this.authenticated.next(true);
    } else {
      this.authenticated.next(false);
    }
    this.orderService.authenticated$
      .pipe(
        takeUntil(this.unsubscribeAll),
        rxMap((authenticated) => {
          if (authenticated) {
            this.authenticated.next(authenticated);
          }
        }),
      )
      .subscribe();
  }

  isLoginForTargetExist(login?: string, phone?: PhoneInput): Observable<any> {
    return this.isLoginForTargetExistGQL
      .fetch({
        input: {
          ...(login ? { login } : {}),
          ...(phone ? { phone } : {}),
          target: { pos: this.storageHelper.getData('posId') },
        },
      })
      .pipe(rxMap(({ data }) => data?.isLoginForTargetExist || { exist: false }));
  }

  forgotPassword(pos: string, email: string): Observable<any> {
    return this.sendForgotPasswordMailToTargetGQL.mutate({ email, target: { pos }, subject: "TSF_FORGOT_PASSWORD" });
  }

  resetPassword(pos: string, password: string, token: string): Observable<any> {
    return this.resetPasswordForAppGql.mutate({ password, token, target: { pos } });
  }
  resetPasswordForTarget(pos: string, password: string, token: string): Observable<any> {
    return this.resetPasswordForTargetGQL.mutate({ password, token, target: { pos }, source: UserEventsSourceEnum.WEB });
  }

  loginForTarget(credentials: { login: string; password: string }): Observable<any> {
    if (this.authenticated.value) {
      this.appService.isSignedIn = true;
      return throwError(() => new Error('User is already logged in.'));
    }
    return this.loginForTargetGQL.fetch({ ...omit(credentials, 'rememberMe'), target: { pos: this.storageHelper.getData('posId') }, source: UserEventsSourceEnum.WEB }).pipe(
      switchMap((res) => {
        if (res.data) {
          this.storageHelper.setData({ [this.ACCESS_TOKEN]: res.data.loginForTarget.accessToken });
          const cartIems = this.appService.getCart();
          this.authenticated.next(true);
          const products = map(cartIems, (product) => {
            return {
              barcode: product?.barcode?.id,
              quantity: product?.quantity,
              ...(product?.productAttributesValues?.length ? { productAttributesValues: product?.productAttributesValues } : {}),
            };
          });
          this.appService.isSignedIn = true;
          if (cartIems?.length) {
            return forkJoin([this.currentUserGQL.fetch(), this.orderService.addProductsToCart(products)]);
          }
          return forkJoin([this.currentUserGQL.fetch(), this.orderService.findShoppingCartByUserAndTarget(this.storageHelper.getData('posId'))]);
        }
        return of(res);
      }),
      rxMap((res: any) => {
        if (res?.[0]?.data) {
          this.appService.clearCart();
          this.userService.user$ = res?.[0]?.data.currentUser;
          this.storageHelper.setData({ currentUserId: res?.[0]?.data.currentUser.id });
          this.authenticated.next(true);
          this.appService.isSignedIn = true;
        }
        return res;
      }),
    );
  }

  simpleLoginForTarget(
    credentials: { login: string; password: string },
    posId: string
  ): Observable<any> {
    if (this.authenticated.value) {
      this.appService.isSignedIn = true;
      return throwError(() => new Error('User is already logged in.'));
    }

    const variables = {
      login: credentials.login,
      password: credentials.password,
      target: { pos: posId }
    };

    return this.loginForTargetGQL.fetch(variables as any).pipe(
      switchMap((res) => {
        const loginData = res.data?.loginForTarget;
        if (!loginData?.accessToken) {
          return throwError(() => new Error('Login failed'));
        }
        this.authenticated.next(true);
        this.appService.isSignedIn = true;
        return of(loginData);
      })
    );
  }

  updateCurrentUserPassword(oldPassword: string, newPassword: string): Observable<UserType> {
    return this.updateCurrentUserPasswordGQL.mutate({ oldPassword, newPassword }).pipe(
      rxMap(({ data }: any) => {
        return data.updateCurrentUserPassword;
      }),
    );
  }

  checkLoginForTarget(pos: string, password: string, login: string): Observable<any> {
    return this.loginForTargetGQL.fetch({ login, password, target: { pos }, source: UserEventsSourceEnum.WEB }).pipe(
      rxMap((res) => {
        if (res.data) {
          return res.data.loginForTarget;
        }
        return null;
      }),
    );
  }

  signOut(): Observable<any> {
    this.saveCurrentCorporateUserStatusGQL
      .mutate({ status: UserStatus.OFFLINE, target: { pos: this.storageHelper.getData('posId') } })
      .subscribe(() => {
        if (this.mouseEvents) {
          this.mouseEvents.unsubscribe();
        }
        if (this.timer) {
          this.timer.unsubscribe();
        }
        this.storageHelper.remove('token');
        this.storageHelper.remove('currentUserId');
        this.authenticated.next(false);
        this.router.navigate(['/']);
        this.orderService.orders$ = null;
      });
    return of(true);
  }

  deleteUser(deleteData: { password: string; reason: string; description: string }): Observable<any> {
    const userId = localStorage.getItem('userId');

    if (!userId) {
      return throwError(() => new Error('User ID not found'));
    }

    return this.deleteUserGQL.mutate({
      password: deleteData.password,
      id: userId,
      reason: deleteData.reason as DeleteUserReasonEnum,
      description: deleteData.description
    }).pipe(
      take(1),
      switchMap((result) => {
        if (result?.data?.deleteUser?.success) {
          if (this.mouseEvents) {
            this.mouseEvents.unsubscribe();
          }
          if (this.timer) {
            this.timer.unsubscribe();
          }

          this.storageHelper.remove('token');
          this.storageHelper.remove('userId');
          this.storageHelper.remove('currentUserId');
          this.storageHelper.remove('wholesalerId');
          this.storageHelper.remove('userEmail');
          this.storageHelper.remove('pos_owner');

          this.authenticated.next(false);

          this.orderService.orders$ = null;

          console.log('✅ Account deleted successfully');

          return of(result);
        }
        return of(result);
      }),
      rxMap((result: any) => {
        return result;
      })
    );
  }

  registerForTarget(input: UserWithTargetInput, role: UserRole[] = [UserRole.CONSUMER]): Observable<any> {
    return this.registerForTargetGQL.mutate({ input: { ...input, target: { pos: this.storageHelper.getData('posId') }, roles: role } }).pipe(
      take(1),
      switchMap((res) => {
        if (res.data) {
          this.storageHelper.setData({ [this.ACCESS_TOKEN]: res.data.registerForTarget.accessToken });
          this.authenticated.next(true);
          this.appService.isSignedIn = true;
          const cartIems = this.appService.getCart();
          const products = map(cartIems, (product) => {
            return {
              barcode: product?.barcode?.id,
              quantity: product?.quantity,
              ...(product?.productAttributesValues?.length ? { productAttributesValues: product?.productAttributesValues } : {}),
            };
          });
          if (cartIems?.length) {
            return forkJoin([this.currentUserGQL.fetch(), this.orderService.addProductsToCart(products)]);
          }
          return forkJoin([this.currentUserGQL.fetch(), this.orderService.findShoppingCartByUserAndTarget(this.storageHelper.getData('posId'))]);
        }
        return of(res);
      }),
      rxMap((result: any) => {
        if (result?.[0].data?.currentUser) {
          this.appService.clearCart();
          this.userService.user$ = result?.[0].data?.currentUser;
          this.storageHelper.setData({ currentUserId: result?.[0].data?.currentUser.id });
        }
        return result;
      }),
    );
  }



  registerForTargetWithWholeSaler(
    input: UserWithTargetInput,
    role: UserRole[] = [UserRole.CONSUMER],
  ): Observable<any> {
    return this.registerForTargetWithWholesalerGQL.mutate({
      input: { ...input, roles: role }
    }).pipe(
      take(1),
      switchMap((res) => {
        if (res.data) {
          this.storageHelper.setData({
            [this.ACCESS_TOKEN]: res.data.registerForTargetWithWholesaler.accessToken
          });
          this.authenticated.next(true);
          this.appService.isSignedIn = true;
        }
        return of(res);
      }),
    );
  }










  // /**
  //  * Unlock session
  //  *
  //  * @param credentials
  //  */
  unlockSession(credentials: { login: string; password: string }, target: TargetAciInput): Observable<any> {
    return this.loginForTargetGQL.fetch({ ...credentials, target, source: UserEventsSourceEnum.WEB });
  }

  check(): Observable<boolean> {
    if (this.authenticated.value) {
      return of(true);
    }
    if (!this.accessToken) {
      return of(false);
    }
    if (AuthUtils.isTokenExpired(this.accessToken)) {
      return of(false);
    }
    return of(false);
  }
}
